import Pagination from '../../components/Common/Pagination';
import { TableLoader, Spinner } from '../../components/Common/Loader';
import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getUnits, createUnit, updateUnit } from '../../api/productMasters';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';

interface UnitItem {
  id: number;
  name: string;
  sapCode: string;
  symbol: string;
  status: 'Active' | 'Inactive';
  createdAt: string;
}

export const UnitsList: React.FC = () => {
  const [units, setUnits] = useState<UnitItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [editingItem, setEditingItem] = useState<UnitItem | null>(null);

  // Fetch Units from API
  const fetchUnitsData = async () => {
    try {
      setLoading(true);
      const res = await getUnits();
      if (res && (res.status === 'success' || res.success) && res.data) {
        const rawList = Array.isArray(res.data)
          ? res.data
          : ((res.data as any)?.data || (res.data as any)?.units || []);

        if (Array.isArray(rawList)) {
          const mapped: UnitItem[] = rawList.map((item: any) => ({
            id: item.id,
            name: item.name || item.unitName || item.unit_name || '',
            sapCode: item.sapCode || '-',
            symbol: item.symbol || '-',
            status: (item.status === 1 || item.status === '1' || item.status === 'Active') ? 'Active' : 'Inactive',
            createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : '-',
          }));
          setUnits(mapped);
        }
      }
    } catch (err) {
      console.error('Error fetching units list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnitsData();
  }, []);

  const [activeTab, setActiveTab] = useState<'Active' | 'Inactive' | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    sapCode: '',
    symbol: '',
    status: 'Active' as 'Active' | 'Inactive',
  });

  // Dynamic active/inactive counts
  const activeCounts = useMemo(() => units.filter(u => u.status === 'Active').length, [units]);
  const inactiveCounts = useMemo(() => units.filter(u => u.status === 'Inactive').length, [units]);

  // Filtering Logic
  const filteredUnits = useMemo(() => {
    return units.filter((item) => {
      if (activeTab === 'Active' && item.status !== 'Active') return false;
      if (activeTab === 'Inactive' && item.status !== 'Inactive') return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          item.sapCode.toLowerCase().includes(query) ||
          item.symbol.toLowerCase().includes(query) ||
          String(item.id).includes(query)
        );
      }
      return true;
    });
  }, [units, activeTab, searchQuery]);

  // Paginated Items
  const paginatedUnits = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredUnits.slice(startIndex, startIndex + entriesLimit);
  }, [filteredUnits, currentPage, entriesLimit]);

  const handleOpenAdd = () => {
    setEditingItem(null);
    setFormData({ name: '', sapCode: '', symbol: '', status: 'Active' });
    setView('add');
  };

  const handleOpenEdit = (item: UnitItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      sapCode: item.sapCode === '-' ? '' : item.sapCode,
      symbol: item.symbol === '-' ? '' : item.symbol,
      status: item.status || 'Active',
    });
    setView('edit');
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Name', 'SAP Code', 'Symbol', 'Status', 'Created At'];
    const rows = filteredUnits.map((item) => [
      item.id,
      item.name,
      item.sapCode,
      item.symbol,
      item.status,
      item.createdAt,
    ]);
    exportToCSV(`units_${activeTab.toLowerCase()}`, headers, rows);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Name is required.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name,
        unitName: formData.name,
        sapCode: formData.sapCode || null,
        symbol: formData.symbol || null,
        status: formData.status === 'Active' ? 1 : 0,
      };

      if (editingItem) {
        // Update Unit: PUT /api/v1/erp/products/units/:id
        await updateUnit(editingItem.id, payload);
      } else {
        // Create Unit: POST /api/v1/erp/products/units
        await createUnit(payload);
      }

      await fetchUnitsData();
      setActiveTab('All');
      setView('list');
    } catch (err: any) {
      console.error('Error saving unit:', err);
      alert(err.message || 'Failed to save unit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (view === 'add' || view === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button 
              type="button"
              onClick={() => setView('list')}
              className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
              {view === 'edit' ? 'Edit Unit' : 'Add Unit'}
            </h1>
          </div>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {/* Name */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Name*</label>
              <input 
                type="text" 
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white" 
              />
            </div>

            {/* SAP Code */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">SAP Code</label>
              <input 
                type="text" 
                value={formData.sapCode}
                onChange={(e) => setFormData({...formData, sapCode: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white" 
              />
            </div>

            {/* Symbol */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Symbol</label>
              <input 
                type="text" 
                value={formData.symbol}
                onChange={(e) => setFormData({...formData, symbol: e.target.value})}
                className="w-full border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 bg-white" 
              />
            </div>

            {/* Status Dropdown */}
            <div>
              <label className="block text-slate-800 font-bold text-xs mb-1.5">Status</label>
              <div className="relative">
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  className="w-full bg-[#F5F5F5] border border-[#828282] rounded-2xl py-2.5 px-4 outline-none focus:go_erp-border text-xs font-semibold text-slate-800 appearance-none cursor-pointer"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center pt-4 col-span-1 md:col-span-2">
              <button 
                type="submit" 
                disabled={submitting}
                className="bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold py-3 px-16 rounded-xl text-sm transition-all cursor-pointer shadow-md hover:shadow-[#DCA10D]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting && <Spinner size="sm" color="text-white" />}
                {submitting ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Control Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {/* Title and Back */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => window.history.back()}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm" title="Go Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight">
            Units
          </h1>
        </div>

        {/* Status Tabs */}
        <div className="flex bg-white p-1 rounded-full border border-slate-200 w-fit">
          {(['All', 'Active', 'Inactive'] as const).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold transition-all duration-150 cursor-pointer ${
                  isActive
                    ? 'go_erp-bg text-white shadow-sm'
                    : 'text-slate-655 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleOpenAdd}
            className="go_erp-bg text-white text-sm px-4 py-1.5 rounded-xl flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Units
          </button>
          <CSVButton onClick={handleExportCSV} />
        </div>
      </div>

      {/* Counts */}
      <div className="flex items-center justify-end gap-4 text-xs font-bold text-slate-800">
        <span>Active Counts: <span className="font-extrabold">{activeCounts}</span></span>
        <span>Inactive Counts: <span className="font-extrabold">{inactiveCounts}</span></span>
      </div>

      {/* Table Main Card */}
      <div className="bg-white rounded-2xl overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-slate-200 text-xs font-bold text-slate-700 rounded px-2 py-1 outline-none focus:border-slate-400"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-xs font-semibold text-slate-500">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-slate-200 text-xs rounded-full py-1.5 pl-8 pr-3 outline-none focus:border-slate-400 w-44 transition-all"
              />
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="overflow-x-auto select-none">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#FFF8E1]">
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">ID</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Name</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">SAP Code</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Symbol</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Status</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f]">Created At</th>
                <th className="px-4 py-4 text-xs font-semibold text-[#1f1f1f] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECF2]">
              {loading ? (
                <TableLoader colSpan={7} text="Loading..." />
              ) : paginatedUnits.length > 0 ? (
                paginatedUnits.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.id}</td>
                    <td className="px-4 py-4 text-xs font-bold text-slate-800">{item.name}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.sapCode}</td>
                    <td className="px-4 py-4 text-xs text-[#828282] font-semibold">{item.symbol}</td>
                    <td className="px-4 py-4 text-xs text-[#828282]">{item.status}</td>
                    <td className="px-4 py-4 text-xs text-[#828282] whitespace-nowrap">{item.createdAt}</td>
                    <td className="px-4 py-4 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenEdit(item)}
                            className="p-1.5 text-slate-500 hover:text-[#B71C1C] transition-colors cursor-pointer rounded-lg"
                            title="View"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                          </button>
                          <button
                            onClick={() => handleOpenEdit(item)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer rounded-lg"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                        </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                    No data available in table
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredUnits.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>
    </div>
  );
};
export default UnitsList;

